class Pagination {
  /**
   * @param {string|HTMLElement} targetSelector - Container, in den die Pagination gerendert wird
   * @param {Object} data - Paginierungsdaten
   * @param {Object} [options] - Zusätzliche Optionen
   */
  constructor(targetSelector, data = {}, options = {}) {
    this.container =
      typeof targetSelector === 'string'
        ? document.querySelector(targetSelector)
        : targetSelector;

    this.data = this.normalizeData(data);
    this.maxVisible = Number(options.maxVisible) || 5;
    this.searchString = options.searchString || '';
    this.onPageChange =
      typeof options.onPageChange === 'function'
        ? options.onPageChange
        : (pageNum) => {
          if (this.data.baseUrl) {
            // automatische Weiterleitung
            window.location.href = `${this.data.baseUrl}?${this.searchString}#page=${pageNum}`;
          }
          else {
            console.warn(`[Pagination] Page change → ${pageNum} (no baseUrl set)`);
          }
        };

    if (!this.container || !this.data.totalPages || this.data.totalPages <= 1) {
      if (this.container) this.container.innerHTML = '';
      return;
    }

    this.render();
  }

  // 🧠 Normalisiert verschiedene Datenstrukturen zu einem einheitlichen Schema
  normalizeData(raw) {
    return {
      currentPage: Number(raw.currentPage || 1),
      totalPages: Number(raw.totalPages || raw.countPages || 1),
      baseUrl: raw.baseUrl || '',
      labels: {
        first: raw.labels?.first || 'First page',
        previous: raw.labels?.previous || 'Previous page',
        page: raw.labels?.page || 'Page',
        next: raw.labels?.next || 'Next page',
        last: raw.labels?.last || 'Last page',
      },
      parts: raw.parts || this.generateParts(raw),
    };
  }

  // 🔧 Wenn keine parts übergeben sind, generiere sie automatisch
  generateParts(raw) {
    const total = Number(raw.totalPages || raw.countPages || 1),
      current = Number(raw.currentPage || 1),
      parts = [];

    for (let i = 1; i <= total; i++) {
      parts.push({
        title: i,
        is_link: i !== current,
        url: raw.baseUrl
          ? `${raw.baseUrl}#page=${i}`
          : `#page=${i}`,
      });
    }

    return parts;
  }

  getDisabledState(type) {
    const { currentPage, totalPages } = this.data,
      disabled = (() => {
        switch (type) {
          case 'first':
          case 'previous': return currentPage === 1;
          case 'next':
          case 'last': return currentPage === totalPages;
          default: return false;
        }
      })();

    return disabled ? ' disabled aria-disabled="true"' : '';
  }

  get visiblePages() {
    const { totalPages, currentPage } = this.data,
      max = this.maxVisible;

    if (totalPages <= max) return Array.from({ length: totalPages }, (_, i) => i + 1);

    // Mobile use-case with first/last nav buttons:
    // keep only 3 numeric buttons, but show the opposite edge page at boundaries.
    if (max === 3) {
      if (currentPage === 1) {
        return [1, 2, '…', totalPages];
      }

      if (currentPage === 2) {
        return [1, 2, 3, '…', totalPages];
      }

      if (currentPage === totalPages - 1) {
        return [1, '…', totalPages - 2, totalPages - 1, totalPages];
      }

      if (currentPage === totalPages) {
        return [1, '…', totalPages - 1, totalPages];
      }

      return ['…', currentPage - 1, currentPage, currentPage + 1, '…'];
    }

    const windowSize = Math.max(1, max),
      half = Math.floor(windowSize / 2);

    let start = currentPage - half,
      end = start + windowSize - 1;

    if (start < 1) {
      start = 1;
      end = windowSize;
    }

    if (end > totalPages) {
      end = totalPages;
      start = Math.max(1, end - windowSize + 1);
    }

    const pages = [];

    if (start > 1) pages.push('…');

    for (let i = start; i <= end; i++) pages.push(i);

    if (end < totalPages) pages.push('…');

    return pages;
  }

  renderNavButton(key, icon, pageNum, label, disabled) {
    return `
      <li aria-hidden="${disabled ? 'true' : 'false'}">
        <button
          class="pagination__button pagination__button--${key} js-page-btn"
          aria-label="${label}"
          ${disabled}
          ${disabled ? '' : `data-page="${pageNum}"`}
          type="button"
        >
          <svg aria-hidden="true" focusable="false"><use href="#icon-${icon}" /></svg>
          <span class="visually-hidden">${label}</span>
        </button>
      </li>`;
  }

  render() {
    const d = this.data,
      showFirstLast = d.totalPages > 2,
      current = d.currentPage,
      total = d.totalPages,
      lbl = d.labels;

    let html = `
      <ul class="pagination__list list-unstyled" role="navigation" aria-label="Pagination">
        ${showFirstLast ? this.renderNavButton('first', 'first', 1, lbl.first, this.getDisabledState('first')) : ''}
        ${this.renderNavButton('previous', 'previous', Math.max(1, current - 1), lbl.previous, this.getDisabledState('previous'))}
        ${this.visiblePages.map((n) => {
          if (n === '…') return `<li class="pagination__ellipsis" aria-hidden="true">…</li>`;
          const isActive = n === current;
          return `
                <li>
                  <button
                    class="pagination__button js-page-btn${isActive ? ' is-active' : ''}"
                    ${isActive ? 'aria-current="page" aria-disabled="true" disabled' : `data-page="${n}"`}
                    aria-label="${lbl.page} ${n}"
                    type="button"
                  >
                    ${n}
                    <span class="visually-hidden">${lbl.page} ${n}</span>
                  </button>
                </li>`;
        }).join('')}
        ${this.renderNavButton('next', 'next', Math.min(total, current + 1), lbl.next, this.getDisabledState('next'))}
        ${showFirstLast ? this.renderNavButton('last', 'last', total, lbl.last, this.getDisabledState('last')) : ''}
      </ul>`;

    if (this.container) {
      this.container.innerHTML = html;
      this.bindEvents();
    }

    return html;
  }

  bindEvents() {
    if (!this.container) return;

    this.container.addEventListener('click', (e) => {
      const btn = e.target.closest('.js-page-btn');

      if (!btn || btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;

      const page = Number(btn.dataset.page);

      if (Number.isInteger(page) && page >= 1 && page <= this.data.totalPages) {
        this.onPageChange(page);
      }
    });
  }

  // 🔁 Optional: Update-Funktion für AJAX / SPA
  update(newData) {
    this.data = this.normalizeData(newData);
    this.render();
  }

  setCurrentPage(page) {
    if (!Number.isInteger(page) || page < 1 || page > this.data.totalPages) {
      return;
    }

    if (this.data.currentPage === page) {
      return;
    }

    this.data.currentPage = page;
    this.render();
  }
}
